<script setup lang="ts">
import { useOverlayScrollLock } from '@ui/utils/useOverlayScrollLock'

const props = withDefaults(
  defineProps<{
    closable?: boolean
    useVShow?: boolean
    zIndex?: number
    teleportDisabled?: boolean
    lockScroll?: boolean
  }>(),
  {
    closable: true,
    useVShow: false,
    zIndex: 300,
    teleportDisabled: false,
    lockScroll: true,
  },
)

const show = defineModel<boolean>('show', { default: false })
const hasOpened = ref(show.value)
const shouldRender = computed(() => (props.useVShow ? hasOpened.value : show.value))
const shouldShow = computed(() => !props.useVShow || show.value)

watch(show, value => {
  if (value) {
    hasOpened.value = true
  }
})

useOverlayScrollLock(computed(() => props.lockScroll && show.value))

function onClick() {
  if (!props.closable) {
    return
  }
  show.value = false
}
</script>

<template lang="pug">
teleport(to="body" :disabled="teleportDisabled")
  Transition(name="backdrop")
    //- Opacity needs a painted wrapper to fade both the backdrop and its content.
    .backdrop-transition.position-relative(v-if="shouldRender" v-show="shouldShow" :style="{ zIndex }")
      .backdrop.position-fixed.t-0.b-0.l-0.r-0(@click.self="onClick")
      .content.flex-center-center.position-fixed.t-0.b-0.l-0.r-0(v-if="$slots.default" @click.self="onClick")
        slot
</template>

<style scoped>
.backdrop {
  opacity: 0.15;
  background: var(--cue-static-neutral-inverse);
}

.content {
  place-items: center;
  place-content: center;
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .backdrop-enter-active,
  .backdrop-leave-active {
    transition-duration: 0s;
  }
}
</style>
